/**
 * Match prediction card with debounced autosave + keyboard nav + quick-fill chips.
 *
 * Why autosave is safe: POST /api/pools/:slug/predictions is an upsert with
 * an atomic SQL lock_time check (server/routes-elysia/predictions.ts ~125–230).
 * Repeated calls with the same body are idempotent; locked-match writes are
 * rejected at the DB layer.
 */

import { memo, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Save, Trophy, Hash } from 'lucide-react';
import { cn, formatTime, getStageDisplayName, getPredictionResultDisplay } from '../../lib/utils';
import { predictionsApi, type ApiError } from '../../lib/api';
import { Card } from '../ui/card';
import Badge from '../ui/badge';
import Button from '../ui/Button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import Kbd from '../ui/kbd';
import { useLockCountdown } from '../../hooks/useLockCountdown';
import { predictionKeys } from '../../hooks/usePredictions';
import type { Match, Prediction } from '../../../shared/types';
import PredictionStatusBadge, { type PredictionStatus } from './PredictionStatusBadge';
import QuickFillChips, { rememberScore } from './QuickFillChips';

const AUTOSAVE_DELAY_MS = 600;
const AUTOSAVE_RETRY_DELAY_MS = 1500;

export interface MatchPredictionCardProps {
  poolSlug: string;
  match: Match;
  prediction?: Prediction;
  /** Optional callback fired when this card transitions to/from "pending" state. */
  onPendingChange?: (matchId: number, pending: boolean) => void;
}

interface ScoreFields {
  home: string;
  away: string;
}

function parseScore(raw: string): number | null {
  if (raw === '') return null;
  const n = Number.parseInt(raw, 10);
  if (Number.isNaN(n) || n < 0 || n > 20) return null;
  return n;
}

function MatchPredictionCardImpl({
  poolSlug,
  match,
  prediction,
  onPendingChange,
}: MatchPredictionCardProps) {
  const queryClient = useQueryClient();
  const homeId = useId();
  const awayId = useId();

  const [scores, setScores] = useState<ScoreFields>({
    home: prediction?.homeScore?.toString() ?? '',
    away: prediction?.awayScore?.toString() ?? '',
  });
  const [status, setStatus] = useState<PredictionStatus>(
    prediction ? { kind: 'saved', at: Date.now() } : { kind: 'pending' }
  );

  // Reflect server-side prediction updates that arrive after our optimistic state.
  useEffect(() => {
    setScores({
      home: prediction?.homeScore?.toString() ?? '',
      away: prediction?.awayScore?.toString() ?? '',
    });
  }, [prediction?.homeScore, prediction?.awayScore]);

  const lock = useLockCountdown(match.lockTime);
  const locked = lock.locked;
  const isFinished = match.status === 'finished';
  const hasResult = match.homeScore !== null && match.awayScore !== null;

  // Per-card mutation. Sharing one mutation across cards would conflate isPending.
  // Retry once on transient failure (network blip / 5xx). After two failures we
  // surface the "Falha — tentar novamente" status and let the user retry manually.
  const mutation = useMutation({
    mutationFn: ({ home, away }: { home: number; away: number }) =>
      predictionsApi.createOrUpdate(poolSlug, match.id, home, away),
    retry: (failureCount, err: ApiError) => {
      if (failureCount >= 1) return false;
      const status = err?.status ?? 0;
      // Retry on network errors (status 0) and transient 5xx; do not retry on
      // 4xx (validation, lock-time, auth — surfacing the error is correct).
      return status === 0 || (status >= 500 && status < 600);
    },
    retryDelay: AUTOSAVE_RETRY_DELAY_MS,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: predictionKeys.byPool(poolSlug) });
      queryClient.invalidateQueries({ queryKey: predictionKeys.stats(poolSlug) });
      setStatus({ kind: 'saved', at: Date.now() });
    },
    onError: (_err: ApiError) => {
      setStatus({ kind: 'error' });
    },
  });

  // Debounced autosave: when both scores are valid and differ from the saved
  // prediction, fire after AUTOSAVE_DELAY_MS of input quiet.
  const lastSavedRef = useRef<{ home: number | null; away: number | null }>({
    home: prediction?.homeScore ?? null,
    away: prediction?.awayScore ?? null,
  });
  useEffect(() => {
    lastSavedRef.current = {
      home: prediction?.homeScore ?? null,
      away: prediction?.awayScore ?? null,
    };
  }, [prediction?.homeScore, prediction?.awayScore]);

  useEffect(() => {
    if (locked || isFinished) return undefined;
    const home = parseScore(scores.home);
    const away = parseScore(scores.away);
    if (home === null || away === null) return undefined;
    if (home === lastSavedRef.current.home && away === lastSavedRef.current.away) {
      return undefined;
    }
    setStatus({ kind: 'saving' });
    const handle = window.setTimeout(() => {
      mutation.reset();
      mutation.mutate(
        { home, away },
        {
          onSuccess: () => {
            lastSavedRef.current = { home, away };
            rememberScore(poolSlug, home, away);
          },
        }
      );
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scores.home, scores.away, locked, isFinished, poolSlug]);

  // Notify parent of pending count changes for the sticky bar.
  useEffect(() => {
    const pending = !locked && !prediction;
    onPendingChange?.(match.id, pending);
  }, [match.id, locked, prediction, onPendingChange]);

  const handleScoreChange = useCallback(
    (which: 'home' | 'away', value: string) => {
      // Allow empty string while typing; restrict to 0–2 digit numbers.
      if (value !== '' && !/^\d{1,2}$/.test(value)) return;
      setScores((prev) => ({ ...prev, [which]: value }));
    },
    []
  );

  const handlePickQuickFill = useCallback(
    (home: number, away: number) => {
      setScores({ home: home.toString(), away: away.toString() });
    },
    []
  );

  // Manual save fallback (Enter or click on Save button).
  const handleManualSave = useCallback(() => {
    if (locked || isFinished) return;
    const home = parseScore(scores.home);
    const away = parseScore(scores.away);
    if (home === null || away === null) return;
    if (home === lastSavedRef.current.home && away === lastSavedRef.current.away) return;
    setStatus({ kind: 'saving' });
    mutation.reset();
    mutation.mutate(
      { home, away },
      {
        onSuccess: () => {
          lastSavedRef.current = { home, away };
          rememberScore(poolSlug, home, away);
        },
      }
    );
  }, [locked, isFinished, scores.home, scores.away, mutation, poolSlug]);

  const homeInputRef = useRef<HTMLInputElement>(null);
  const awayInputRef = useRef<HTMLInputElement>(null);

  // Keyboard handler: Enter forces immediate save.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleManualSave();
        // Move focus to the next match's home score if present.
        const next = (e.currentTarget.closest('[data-match-card]') as HTMLElement | null)
          ?.parentElement?.nextElementSibling?.querySelector<HTMLInputElement>(
            'input[data-score-position="home"]'
          );
        next?.focus();
      } else if (e.key === 'Tab' && e.currentTarget.dataset.scorePosition === 'home' && !e.shiftKey) {
        // Default Tab → away input via DOM order.
      }
    },
    [handleManualSave]
  );

  // Result diff for finished matches.
  let resultDisplay: ReturnType<typeof getPredictionResultDisplay> | null = null;
  if (isFinished && prediction && hasResult) {
    const resHome = match.homeScore ?? 0;
    const resAway = match.awayScore ?? 0;
    let resultType: string;
    if (prediction.homeScore === resHome && prediction.awayScore === resAway) {
      resultType = 'exact';
    } else {
      const predDiff = prediction.homeScore - prediction.awayScore;
      const resDiff = resHome - resAway;
      if (predDiff === resDiff) resultType = 'diff';
      else if (Math.sign(predDiff) === Math.sign(resDiff)) resultType = 'winner';
      else resultType = 'none';
    }
    resultDisplay = getPredictionResultDisplay(resultType);
  }

  // Decide which status to display.
  const displayStatus: PredictionStatus = locked
    ? { kind: 'locked' }
    : status;

  return (
    <Card data-match-card className={cn('p-4 sm:p-5 space-y-3', locked && !isFinished && 'opacity-80')}>
      {/* Header row: stage, time, lock countdown, status */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium">{getStageDisplayName(match.stage)}</span>
          <span aria-hidden="true">•</span>
          <span>{formatTime(match.matchDate)}</span>
          {!locked && lock.totalSeconds > 0 && lock.totalSeconds < 86400 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
                    lock.urgent
                      ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300'
                      : 'bg-neutral-100 dark:bg-neutral-800 text-muted-foreground'
                  )}
                >
                  Trava em {lock.text}
                </span>
              </TooltipTrigger>
              <TooltipContent>Apostas encerram em {lock.text}</TooltipContent>
            </Tooltip>
          )}
        </div>
        <PredictionStatusBadge status={displayStatus} />
      </div>

      {/* Teams + score inputs */}
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0 text-right">
          <span className="font-semibold text-foreground truncate inline-block max-w-full">
            {match.homeTeam}
          </span>
          <label htmlFor={homeId} className="sr-only">
            Gols do {match.homeTeam}
          </label>
        </div>

        <div className="flex items-center gap-1.5">
          <input
            ref={homeInputRef}
            id={homeId}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={2}
            value={scores.home}
            disabled={locked}
            onChange={(e) => handleScoreChange('home', e.target.value)}
            onKeyDown={handleKeyDown}
            data-score-position="home"
            data-match-id={match.id}
            aria-label={`Gols do ${match.homeTeam}`}
            className={cn(
              'w-14 h-14 sm:w-16 sm:h-14 text-center text-2xl font-bold tabular-nums',
              'bg-white dark:bg-neutral-900',
              'border-2 border-neutral-300 dark:border-neutral-600 rounded-xl',
              'transition-all duration-150',
              'hover:border-neutral-400 dark:hover:border-neutral-500',
              'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:focus:border-primary-400',
              'disabled:bg-neutral-100 dark:disabled:bg-neutral-800 disabled:cursor-not-allowed disabled:text-muted-foreground'
            )}
          />
          <span className="text-muted-foreground text-xl font-light" aria-hidden="true">
            ×
          </span>
          <input
            ref={awayInputRef}
            id={awayId}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={2}
            value={scores.away}
            disabled={locked}
            onChange={(e) => handleScoreChange('away', e.target.value)}
            onKeyDown={handleKeyDown}
            data-score-position="away"
            data-match-id={match.id}
            aria-label={`Gols do ${match.awayTeam}`}
            className={cn(
              'w-14 h-14 sm:w-16 sm:h-14 text-center text-2xl font-bold tabular-nums',
              'bg-white dark:bg-neutral-900',
              'border-2 border-neutral-300 dark:border-neutral-600 rounded-xl',
              'transition-all duration-150',
              'hover:border-neutral-400 dark:hover:border-neutral-500',
              'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:focus:border-primary-400',
              'disabled:bg-neutral-100 dark:disabled:bg-neutral-800 disabled:cursor-not-allowed disabled:text-muted-foreground'
            )}
          />
        </div>

        <div className="flex-1 min-w-0">
          <span className="font-semibold text-foreground truncate inline-block max-w-full">
            {match.awayTeam}
          </span>
          <label htmlFor={awayId} className="sr-only">
            Gols do {match.awayTeam}
          </label>
        </div>
      </div>

      {/* Quick-fill chips + manual save action */}
      {!locked && !isFinished && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <QuickFillChips
            poolSlug={poolSlug}
            onPick={handlePickQuickFill}
            disabled={locked}
          />
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Kbd>Enter</Kbd>
                  para salvar
                </span>
              </TooltipTrigger>
              <TooltipContent>Pressione Enter para salvar imediatamente</TooltipContent>
            </Tooltip>
            <Button
              size="sm"
              variant="ghost"
              icon={status.kind === 'saved' ? <CheckCircle2 /> : <Save />}
              iconPosition="left"
              onClick={handleManualSave}
              disabled={
                locked ||
                isFinished ||
                parseScore(scores.home) === null ||
                parseScore(scores.away) === null
              }
              aria-label="Salvar palpite agora"
            >
              Salvar
            </Button>
          </div>
        </div>
      )}

      {/* Result for finished matches */}
      {isFinished && hasResult && (
        <div className="border-t border-neutral-200 dark:border-neutral-800 pt-3 flex items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
            <Hash className="w-3.5 h-3.5" aria-hidden="true" />
            Resultado:{' '}
            <span className="font-semibold text-foreground tabular-nums">
              {match.homeScore} × {match.awayScore}
            </span>
          </div>
          {resultDisplay && (
            <Badge
              variant={
                resultDisplay.points >= 10
                  ? 'success'
                  : resultDisplay.points >= 7
                    ? 'info'
                    : resultDisplay.points >= 3
                      ? 'warning'
                      : 'destructive'
              }
            >
              <Trophy className="w-3 h-3" aria-hidden="true" />
              {resultDisplay.label}
              {resultDisplay.points > 0 && ` +${resultDisplay.points}`}
            </Badge>
          )}
        </div>
      )}
    </Card>
  );
}

const MatchPredictionCard = memo(MatchPredictionCardImpl);
MatchPredictionCard.displayName = 'MatchPredictionCard';

export default MatchPredictionCard;
